// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid2, IconButton, Stack, Typography } from '@mui/material';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TFunction } from 'i18next';
// Components, Layouts, Pages
import { BaseDatePicker, BaseLabel, BaseTable, Loading, UnderDevelopment } from '~/components/common';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import { IHeaderCellType } from '~/utils/interface/common';
import { IPaginationResponse } from '~/utils/interface/base';
import { DiscountTypeEnum, ReportTypeEnum } from '~/utils/enum/common';
import { IDiscountUsageReport, IQueryParamListExport } from '~/utils/interface/reports';
import { exportDataReport, getListDiscountUsageReport } from '~/thunks/reports/reportsThunk';
import { formatCurrencyUSD, formatTitleCase, getFullName } from '~/utils/helper/common';
import { BACK_TO_PREVIOUS_PAGE, DEFAULT_CURRENT_PAGE, DEFAULT_LIMIT_PAGE } from '~/utils/constants/common';
// Styles, images, icons
import styles from './DiscountUsageReport.module.scss';
import { icons } from '~/assets';

const cx = classNames.bind(styles);

const columns = (t: TFunction): IHeaderCellType<IDiscountUsageReport>[] => {
  return [
    {
      id: 'orderNumber',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_discount_usage_table_order_id_title')}</Box>
        </Stack>
      ),
      render: (record: IDiscountUsageReport) => (
        <Stack>
          <Typography className={cx('orderNumber')}>{record?.orderNumber}</Typography>
        </Stack>
      ),
    },
    {
      id: 'client',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_discount_usage_table_client_name_title')}</Box>
        </Stack>
      ),
      render: (record: IDiscountUsageReport) => {
        return getFullName({ firstName: record?.client?.firstName, lastName: record?.client?.lastName });
      },
    },
    {
      id: 'discountType',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_discount_usage_table_type_title')}</Box>
        </Stack>
      ),
      render: (record: IDiscountUsageReport) => {
        return formatTitleCase(record?.coupon.discountType);
      },
    },
    {
      id: 'discountValue',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_discount_usage_table_value_title')}</Box>
        </Stack>
      ),
      render: (record: IDiscountUsageReport) => {
        return DiscountTypeEnum?.PERCENTAGE === record?.coupon.discountType
          ? `${record?.coupon.discountRate}%`
          : formatCurrencyUSD({ value: record?.coupon.discountRate, showDecimal: true });
      },
    },
    {
      id: 'total',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_discount_usage_origin_amount_title')}</Box>
        </Stack>
      ),
      render: (record: IDiscountUsageReport) => {
        return formatCurrencyUSD({ value: record?.total, showDecimal: true });
      },
    },
    {
      id: 'couponAmount',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_discount_usage_table_amount_title')}</Box>
        </Stack>
      ),
      render: (record: IDiscountUsageReport) => {
        return formatCurrencyUSD({ value: record?.couponAmount, showDecimal: true });
      },
    },
    {
      id: 'finalAmount',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_discount_usage_table_final_amount_title')}</Box>
        </Stack>
      ),
      render: (record: IDiscountUsageReport) => {
        return formatCurrencyUSD({ value: record?.finalAmount, showDecimal: true });
      },
    },
    {
      id: 'couponCode',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_discount_usage_table_code_title')}</Box>
        </Stack>
      ),
      render: (record: IDiscountUsageReport) => {
        return record?.coupon.name;
      },
    },
  ];
};

const DiscountUsageReport = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/reports', 'common/base']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [discountUsageList, setDiscountUsageList] = useState<IDiscountUsageReport[]>([]);
  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [paramsObject, setParamsObject] = useState<IQueryParamListExport>({
    page: DEFAULT_CURRENT_PAGE,
    limit: DEFAULT_LIMIT_PAGE,
  });
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListDiscountUsage();
  }, [paramsObject.page]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListDiscountUsage = () => {
    setIsLoading(true);

    dispatch(getListDiscountUsageReport(paramsObject))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { responses, pagination } = res.data;

        setDiscountUsageList(responses);
        setPagination(pagination);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleApplyFilter = () => {
    handleGetListDiscountUsage();
  };

  const handleExport = () => {
    setIsLoading(true);

    const params: IQueryParamListExport = {
      ...paramsObject,
      type: ReportTypeEnum.DISCOUNT_USAGE,
    };

    dispatch(exportDataReport(params))
      .unwrap()
      .then((res) => {
        const { data } = res;

        handleDownloadFile(data?.url);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleDownloadFile = (fileUrl: string) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileUrl;
    link.click();
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamsObject({ ...paramsObject, page });
    setSearchParams({ ...params, page: String(page) });
  };

  const handleBack = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
  };
  //#endregion Handle Function

  return (
    <Stack id='reportsDiscountUsagePage' className={cx('container')} spacing={6}>
      <Stack direction='row' spacing={2} alignItems='center'>
        <IconButton color='primary' onClick={handleBack} className={cx('buttonBack')}>
          <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} />
        </IconButton>

        <Stack direction={'column'} spacing={2}>
          <BaseLabel label={t('reports_discount_usage_title')} styleType='bigTitle' />
          <Typography className={cx('description')}>{t('reports_discount_usage_desc')}</Typography>
        </Stack>
      </Stack>

      <Stack direction={'column'} gap={5} className={cx('viewAction')}>
        <Grid2 columnSpacing={'24px'} alignItems={'start'} rowSpacing={'24px'} container>
          <Grid2 size={3}>
            <BaseDatePicker
              mode='date'
              height={40}
              label={t('common_text_start_date', { ns: 'common/base' })}
              value={paramsObject.startDate}
              onChange={(date) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  startDate: date as string,
                }));
              }}
            />
          </Grid2>

          <Grid2 size={3}>
            <BaseDatePicker
              mode='date'
              height={40}
              label={t('common_text_end_date', { ns: 'common/base' })}
              value={paramsObject.endDate}
              onChange={(date) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  endDate: date as string,
                }));
              }}
            />
          </Grid2>
        </Grid2>

        <Stack direction='row' justifyContent={'space-between'} alignItems='center'>
          <Button
            type='button'
            variant='contained'
            className={cx('buttonApply')}
            onClick={handleApplyFilter}
            startIcon={<img src={icons.commonIconFilter} alt={t('common_alt_img', { ns: 'common/base' })} />}
          >
            {t('common_action_apply', { ns: 'common/base' })}
          </Button>

          <Button
            type='button'
            variant='contained'
            className={cx('buttonExport')}
            onClick={handleExport}
            disabled={!discountUsageList.length}
            startIcon={<img src={icons.commonIconExportExcel} alt={t('common_alt_img', { ns: 'common/base' })} />}
          >
            {t('common_button_export_excel', { ns: 'common/base' })}
          </Button>
        </Stack>
      </Stack>

      <Box className={cx('tableContainer')}>
        <BaseTable
          dataSource={discountUsageList}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columns(t)}
          onPageChange={handlePageChange}
        />
      </Box>

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}

      {isLoading && <Loading />}
    </Stack>
  );
};

export default DiscountUsageReport;
